// packages/core/src/badges/badgeEvaluation.ts
// ─────────────────────────────────────────────────────────────────────────
// PURE badge evaluation. No storage, no React, no Redux. The web layer
// gathers a BadgeFacts snapshot from real state and passes it here; this
// module decides which badge ids are satisfied.
//
// Two evaluation surfaces:
//   • evaluateRetroactive(facts) → ids the app can PROVE from durable history.
//     Re-run safely on every boot. Only 'retroactive' badges considered.
//   • Forward-only badges are NOT decided here from history. They are flagged
//     by live signals (see BadgeFacts.forwardSignals) — a one-shot set of
//     event markers the web layer accumulates as the user acts.
//
// Keeping this pure means it is trivially testable and the same logic can run
// server-side later without change.
// ─────────────────────────────────────────────────────────────────────────

import { BADGES } from './badgesRegistry';
import { DISCOVERY_QUIZZES } from '../quizzes';

/**
 * Forward-only signal markers. The web layer sets these to `true` the first
 * time the corresponding live action happens (and persists that fact). They
 * are honest one-way flags — once true, the badge is earned.
 */
export interface ForwardSignals {
	/** Used any lesson Apply action at least once. */
	usedApplyAction?: boolean;
	/** Ran any personalized scenario (Simulate or a personalized tool run). */
	ranPersonalizedScenario?: boolean;
	/** Ran a what-if specifically in Simulate. */
	ranWhatIf?: boolean;
	/** Ran an Impact Lab scenario. */
	ranImpactLab?: boolean;
	/** Generated a freedom projection (Simulate freedom plan / projection). */
	generatedFreedomProjection?: boolean;
	/** Read a debate (V1.1). */
	readDebate?: boolean;
	/** Engaged both sides of a debate (V1.1). */
	sawBothSidesOfDebate?: boolean;
}

/**
 * A snapshot of everything badge evaluation needs. The web layer builds this
 * from localStorage + the redux store; evaluation stays pure.
 */
export interface BadgeFacts {
	// ── Learning history (retroactive-provable) ──────────────────────────
	/** Count of lessons completed (read/complete flag set). */
	lessonsCompletedCount: number;
	/** Count of distinct quizzes/assessments passed. */
	quizzesPassedCount: number;
	/** At least one quiz passed on the FIRST attempt. */
	passedAnyQuizFirstTry: boolean;
	/** At least one module FINAL test passed. */
	passedAnyFinalTest: boolean;
	/** At least one lesson has all active flashcards mastered. */
	masteredAnyLessonFlashcards: boolean;
	/** At least one lesson fully mastered (read + quiz + flashcards). */
	fullyMasteredAnyLesson: boolean;
	/** Count of fully-mastered MODULES (all assessments passed). */
	modulesMasteredCount: number;
	/** Tier ids fully completed (every module in the tier mastered). */
	tiersCompleted: string[];

	/** Most learning actions completed within a single calendar day. */
	maxLearningActionsInOneDay: number;

	// ── Worldviews (retroactive-provable) ────────────────────────────────
	/** Completed at least one lesson tied to a school of thought. */
	completedAnySchoolLesson: boolean;
	/** Distinct schools the user has completed a lesson from. */
	distinctSchoolsCompletedCount: number;

	// ── Consistency (retroactive-provable) ───────────────────────────────
	/** Best streak the app can prove (max of current/longest). */
	bestStreakDays: number;

	// ── Freedom Path (retroactive-provable) ──────────────────────────────
	/** Current level. */
	level: number;

	// ── Run the Numbers (mostly forward-only, one retro fact) ────────────
	/** A FI number has been stored/calculated at least once. */
	hasStoredFiNumber: boolean;

	// ── Post-launch additions (ledger / local-store provable) ────────────
	/** Scored 100% on at least one quiz. */
	acedAnyQuiz: boolean;
	/** Count of distinct Discovery quizzes completed. */
	discoveryQuizzesCompletedCount: number;
	/** Finished at least one Market Replay run. */
	completedMarketReplay: boolean;
	/** Count of distinct learning days Daily Review was fully cleared. */
	dailyReviewsClearedCount: number;
	/** Read at least one school exhibit to the end. */
	completedAnySchoolExhibit: boolean;

	// ── Forward-only live signals ────────────────────────────────────────
	forwardSignals: ForwardSignals;
}

/** A satisfied-badge result with optional progress for cosmetic display. */
export interface BadgeEvalResult {
	earnedIds: string[];
	/** badgeId → current progress count, for locked-card progress display. */
	progress: Record<string, number>;
}

// ── Per-badge predicates ──────────────────────────────────────────────────
// Each returns whether the badge is satisfied given the facts. Forward-only
// badges read exclusively from facts.forwardSignals.

type Predicate = (f: BadgeFacts) => boolean;

const PREDICATES: Record<string, Predicate> = {
	// Getting Started
	first_step: (f) => f.lessonsCompletedCount >= 1,
	checked_yourself: (f) => f.quizzesPassedCount >= 1,
	made_it_real: (f) => !!f.forwardSignals.usedApplyAction,
	first_session: (f) => f.maxLearningActionsInOneDay >= 2,

	// Run the Numbers
	ran_the_numbers: (f) =>
		!!f.forwardSignals.ranPersonalizedScenario ||
		!!f.forwardSignals.ranWhatIf ||
		!!f.forwardSignals.ranImpactLab,
	found_your_number: (f) => f.hasStoredFiNumber,
	tested_a_choice: (f) => !!f.forwardSignals.ranWhatIf,
	saw_the_hidden_cost: (f) => !!f.forwardSignals.ranImpactLab,
	freedom_plan_started: (f) => !!f.forwardSignals.generatedFreedomProjection,
	intuition_tested: (f) => f.discoveryQuizzesCompletedCount >= 1,
	intuition_explorer: (f) =>
		f.discoveryQuizzesCompletedCount >= DISCOVERY_QUIZZES.length,
	market_time_traveler: (f) => f.completedMarketReplay,

	// Mastery
	locked_in_memory: (f) => f.masteredAnyLessonFlashcards,
	first_try: (f) => f.passedAnyQuizFirstTry,
	perfect_score: (f) => f.acedAnyQuiz,
	passed_the_test: (f) => f.passedAnyFinalTest,
	fully_trained: (f) => f.fullyMasteredAnyLesson,
	module_complete: (f) => f.modulesMasteredCount >= 1,
	pattern_builder: (f) => f.modulesMasteredCount >= 3,

	// Consistency
	showing_up: (f) => f.bestStreakDays >= 3,
	one_week_in: (f) => f.bestStreakDays >= 7,
	locked_in: (f) => f.bestStreakDays >= 30,
	daily_reviewer: (f) => f.dailyReviewsClearedCount >= 10,

	// Worldviews
	entered_a_worldview: (f) => f.completedAnySchoolLesson,
	worldview_explorer: (f) => f.distinctSchoolsCompletedCount >= 3,
	exhibit_visitor: (f) => f.completedAnySchoolExhibit,
	tradeoff_thinker: (f) => !!f.forwardSignals.readDebate,
	sees_more_than_one_side: (f) => !!f.forwardSignals.sawBothSidesOfDebate,

	// Freedom Path
	getting_trained: (f) => f.level >= 5,
	foundation_built: (f) => f.tiersCompleted.includes('foundation'),
	seasoned: (f) => f.level >= 10,
	builder_built: (f) => f.tiersCompleted.includes('builder'),
	freedom_ready: (f) => f.tiersCompleted.includes('freedom'),
	advanced_perspective: (f) => f.tiersCompleted.includes('advanced'),
};

// ── Progress reporters (cosmetic) ─────────────────────────────────────────
const PROGRESS: Record<string, (f: BadgeFacts) => number> = {
	pattern_builder: (f) => Math.min(f.modulesMasteredCount, 3),
	worldview_explorer: (f) => Math.min(f.distinctSchoolsCompletedCount, 3),
	intuition_explorer: (f) =>
		Math.min(f.discoveryQuizzesCompletedCount, DISCOVERY_QUIZZES.length),
	daily_reviewer: (f) => Math.min(f.dailyReviewsClearedCount, 10),
	showing_up: (f) => Math.min(f.bestStreakDays, 3),
	one_week_in: (f) => Math.min(f.bestStreakDays, 7),
	locked_in: (f) => Math.min(f.bestStreakDays, 30),
	getting_trained: (f) => Math.min(f.level, 5),
	seasoned: (f) => Math.min(f.level, 10),
};

/**
 * Evaluate every badge against the facts. Returns satisfied ids plus a
 * progress map for cosmetic locked-card display.
 *
 * `release` controls which badges are considered: 'v1' ignores 'v1.1' badges
 * entirely so held badges are never awarded before promotion.
 */
export function evaluateBadges(
	facts: BadgeFacts,
	release: 'v1' | 'v1.1' = 'v1',
): BadgeEvalResult {
	const earnedIds: string[] = [];
	const progress: Record<string, number> = {};

	for (const def of BADGES) {
		if (release === 'v1' && def.release !== 'v1') continue;

		const pred = PREDICATES[def.id];
		if (pred && pred(facts)) earnedIds.push(def.id);

		const prog = PROGRESS[def.id];
		if (prog) progress[def.id] = prog(facts);
	}

	return { earnedIds, progress };
}
